import { useState } from 'react';
import MerchantAssetCard from './components/MerchantAssetCard';
import { useStoreCurrencyQuery } from '@/graphql/queries/storeCurrency.generated';
import { useStoreAssets, StoreAsset } from './useStoreAssets';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { AssetFormDialog } from './components/AssetFormDialog';
import { AssetPagination } from './components/AssetPagination';
import { DEFAULT_CURRENCY } from '@/utils/price';
import i18n from '@/utils/i18n';

export function MerchantCurrentAssets({ storeId }: { storeId: string }) {
  const [selectedAsset, setSelectedAsset] = useState<StoreAsset | null>(null);

  const { data: currencyData } = useStoreCurrencyQuery({
    variables: { id: storeId },
    skip: !storeId,
  });

  const currencySymbol = currencyData?.store?.currency_symbol;

  const [currentPage, setCurrentPage] = useState(1);
  const {
    items: paginatedAssets,
    total,
    totalPages,
    initialLoading,
    error,
  } = useStoreAssets({
    storeId,
    page: currentPage,
  });

  return (
    <div className="tw:p-4 tw:flex tw:flex-col tw:gap-6">
      <div className="tw:flex tw:flex-col tw:gap-2">
        <h4 className="tw:text-4xl tw:font-semibold">
          {i18n.t('Merchants:assets')}
        </h4>
        {!error && (
          <span>
            {total > 0
              ? i18n.t('Assets:assetsReadOnlyDescription')
              : i18n.t('Assets:noAssetsBookedToStoreDescription')}
          </span>
        )}
      </div>
      <div className="tw:w-full tw:flex tw:gap-4 tw:flex-wrap">
        <AssetPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          className="tw:w-full"
        />

        {initialLoading && (
          <>
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="tw:w-[312px] tw:aspect-3/4" />
            ))}
          </>
        )}

        {error && (
          <span className="tw:w-full tw:text-center tw:text-foreground/50">
            {i18n.t('Assets:errorLoadingAssets')}
            {error?.message && (
              <>
                <br />({error.message})
              </>
            )}
          </span>
        )}

        {!initialLoading &&
          paginatedAssets.map((asset) => (
            <MerchantAssetCard
              key={asset.id}
              removedAt={asset.removed_at}
              itemTypeName={asset.item_type.name}
              assetId={asset.id}
              billingInfo={{
                currencySymbol,
                billingFrequency: asset.billing_frequency,
                isBillable: asset.is_billable,
                price: asset.price,
              }}
              imageUrl={asset.item_type.image_url}
              className="tw:w-[312px]"
            >
              <Button
                onClick={() => setSelectedAsset(asset)}
                variant="secondary"
                className="tw:w-full"
              >
                {i18n.t('Assets:viewDetails')}
              </Button>
            </MerchantAssetCard>
          ))}
      </div>
      <AssetFormDialog
        mode="view"
        asset={selectedAsset}
        currencySymbol={currencySymbol || DEFAULT_CURRENCY}
        isOpen={!!selectedAsset}
        onOpenChange={() => setSelectedAsset(null)}
        onboardedDate={null}
      />
    </div>
  );
}
